'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const demoCredentials = [
  { role: 'Member', email: 'marcus.williams@bidtrekpartner.lc', password: 'Partner2025!' },
  { role: 'Admin', email: 'kezia.joseph@bidtrek.lc', password: 'Admin2025!' },
];

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);

      // Check if admin or member
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Login failed');

      const { data: adminRecord } = await supabase
        .from('admin_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      toast.success('Signed in successfully');
      if (adminRecord) {
        router.push('/admin-application-queue');
      } else {
        router.push('/member-dashboard');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {/* Mobile logo */}
      <div className="lg:hidden flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-[#F59E0B] rounded-lg flex items-center justify-center">
          <span className="font-display font-bold text-[#0F0F0F] text-sm">BT</span>
        </div>
        <span className="font-display font-bold text-white text-lg">BidTrek Partner</span>
      </div>

      <div className="mb-8">
        <h2 className="font-display font-bold text-3xl text-white mb-2">Sign in</h2>
        <p className="text-[#9CA3AF] text-sm">
          Access your member dashboard and earnings overview.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email */}
        <div>
          <label className="label">Email Address</label>
          <input
            type="email"
            className={`input-field ${errors.email ? 'input-error' : ''}`}
            placeholder="you@example.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && (
            <p className="text-[#EF4444] text-xs mt-1.5">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label className="label" style={{ marginBottom: 0 }}>Password</label>
            <a href="#" className="text-xs text-[#F59E0B] hover:text-[#FCD34D] transition-colors">
              Forgot password?
            </a>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`input-field pr-12 ${errors.password ? 'input-error' : ''}`}
              placeholder="Enter your password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-white transition-colors p-1"
              onClick={() => setShowPassword(!showPassword)}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.password && (
            <p className="text-[#EF4444] text-xs mt-1.5">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="rememberMe"
            className="w-4 h-4 rounded border-[#2A2A2A] bg-[#111111] accent-[#F59E0B] cursor-pointer"
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="text-sm text-[#9CA3AF] cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full justify-center py-3.5"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
              Signing in...
            </>
          ) : (
            <>
              <Icon name="ArrowRightIcon" size={18} />
              Sign In
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-[#6B7280] mt-6">
        Not a member yet?{' '}
        <Link href="/member-sign-up-flow" className="text-[#F59E0B] hover:text-[#FCD34D] transition-colors font-medium">
          Claim your spot →
        </Link>
      </p>

      {/* Demo credentials removed from UI — accounts still functional */}
    </div>
  );
}